import React, { useCallback, useMemo } from 'react';
import { useFulfillmentDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/store/fulfillmentDashboard.store';
import { DataTable } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/DataTable';
import { EmptyStateContainer } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/OverviewDashboard.styled';
import { DataTableColumn } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/FulfillmentDashboard.types';
import { SkipReasonsCell } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/FulfillmentDashboard.styled';
import { useAnalyticsFilterStore } from 'pages/ReportsPage/store/analyticsFilterView.context';

function renderOtAllowed(value: string | number): React.ReactNode {
  const isYes = String(value) === 'Yes';
  return <span style={{ color: isYes ? '#2E7D32' : '#C62828', fontWeight: 600 }}>{value}</span>;
}

function renderSkipReasons(value: string | number): React.ReactNode {
  return <SkipReasonsCell>{String(value)}</SkipReasonsCell>;
}

function formatSkipReasons(breakdown: Record<string, number>): string {
  if (!breakdown) return 'N/A';
  const entries = Object.entries(breakdown);
  if (entries.length === 0) return 'N/A';
  return entries
    .sort((a, b) => b[1] - a[1])
    .map(([reason, count]) => `${reason} (${count})`)
    .join(', ');
}

const COLUMNS: DataTableColumn[] = [
  { key: 'date', label: 'Date', width: '100px' },
  { key: 'shiftId', label: 'Shift ID', noWrap: true },
  { key: 'location', label: 'Location', sortable: true },
  { key: 'position', label: 'Position', sortable: true },
  { key: 'timeframe', label: 'Timeframe', sortable: true },
  { key: 'totalEligible', label: 'Total Eligible', align: 'center', noWrap: true },
  { key: 'available', label: 'Available', align: 'center' },
  { key: 'otEligible', label: 'OT Eligible', align: 'center', noWrap: true },
  {
    key: 'otAllowed',
    label: 'OT Allowed',

    renderCell: renderOtAllowed,
    align: 'center',
    noWrap: true,
  },
  {
    key: 'skipReasons',
    label: 'Call Out Skip Reasons',
    sortable: false,
    renderCell: renderSkipReasons,
  },
  { key: 'leadTime', label: 'Lead Time', align: 'center', noWrap: true },
];

export const UnfulfilledShiftsTable: React.FC = () => {
  const data = useFulfillmentDashboardStore((s) => s.unfulfilledShiftDetails);
  const currentPage = useFulfillmentDashboardStore((s) => s.unfulfilledShiftDetailPage);
  const totalPages = useFulfillmentDashboardStore((s) => s.unfulfilledShiftDetailTotalPages);
  const isLoading = useFulfillmentDashboardStore((s) => s.unfulfilledShiftDetailLoading);
  const hasError = useFulfillmentDashboardStore((s) => s.unfulfilledShiftDetailError);
  const fetchUnfulfilledShiftDetails = useFulfillmentDashboardStore(
    (s) => s.fetchUnfulfilledShiftDetails,
  );

  const dateRange = useAnalyticsFilterStore((s) => s.dateRange);
  const locationIds = useAnalyticsFilterStore((s) => s.locationIds);
  const positions = useAnalyticsFilterStore((s) => s.positions);
  const employmentTypes = useAnalyticsFilterStore((s) => s.employmentTypes);

  const handlePageChange = useCallback(
    (page: number) => {
      fetchUnfulfilledShiftDetails(page, dateRange, locationIds, positions, employmentTypes);
    },
    [fetchUnfulfilledShiftDetails, dateRange, locationIds, positions, employmentTypes],
  );

  const rows = useMemo(
    () =>
      data?.map((d) => ({
        date: d.date,
        shiftId: d.shiftId,
        location: d.location,
        position: d.position,
        timeframe: d.timeframe,
        totalEligible: d.totalEligible,
        available: d.available,
        otEligible: d.otEligible,
        otAllowed: d.otAllowed,
        skipReasons: formatSkipReasons(d.skipReasonBreakdown),
        leadTime: d.leadTime,
      })) ?? [],
    [data],
  );

  if (hasError) {
    return <EmptyStateContainer>Failed to load unfulfilled shifts data</EmptyStateContainer>;
  }

  if (!data && !isLoading) {
    return <EmptyStateContainer>No data available</EmptyStateContainer>;
  }

  return (
    <DataTable
      columns={COLUMNS}
      rows={rows}
      ariaLabel="Unfulfilled shifts detail table"
      serverPagination={{
        currentPage,
        totalPages,
        onPageChange: handlePageChange,
        isLoading,
      }}
    />
  );
};
